import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Router } from '@angular/router';
import { DType, DTypeStatus, JsonReply, NextLocator, Partaker, SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, tap, throwError } from 'rxjs';
import { DTYPE_BRAND_URI, DTYPE_CUSTOMER_URI, DTYPE_PROJECT_URI, USER_URI } from '../projects/projects.service';
import { NextBrand, NextCustomer, NextProject, UserProfile, UserProfilePayload } from '../projects/projects.types';
import {
    AvailabilityStatusEnum,
    BusOdc,
    ChangeServiceStatusReply, ChangeServiceWorkplaceUserAssignmentJobRequest, CloneServiceBody, CloneServiceReply, DeleteNextServicioMissionInfoReply,
    DeleteServiceJobReply,
    DismissServiceUserAssignmentRequest,
    DownloadResourcesModelBody,
    DownloadSearchAbsencesXSSFRequest,
    FlushAbsenceReviewReply,
    FlushAbsenceReviewRequest,
    CancelAbsenceDayReply,
    CancelAbsenceDayRequest,
    FlushDailyPayrollRequest,
    FlushMonthlyPayrollRequest,
    FlushNextServicioMissionInfoBody,
    FlushNextServicioMissionInfoReply,
    FlushNextServicioReply,
    FlushPlanningAreaReply,
    FlushPlanningAreaRequest,
    FlushPresenceConfiguration,
    FlushServiceJobReply,
    FlushServiceUserAssignmentListBody,
    FlushServiceUserAssignmentListReply,
    FlushUser2NextServicioReply,
    FlushUserAreaBody, ImportNextServicioBody,
    ImportNextServicioResponse, List,
    MissionInfo,
    NextServicio,
    NextServicioUserAreaFront,
    Pagination,
    PlanningArea, RequestServiceSynchronizationBody,
    RequestServiceSynchronizationReply,
    RetrieveAbsenceFormReply,
    RetrieveAbsenceFormsRequest,
    RetrieveAvailabilityControlSearchOptionsReply, RetrieveDailyPayrollOptionsRequest, RetrieveDailyPayrollReply,
    RetrieveDailyPayrollRequest,
    RetrieveDailyPayrollSummaryReply,
    RetrieveDailyPayrollSummaryRequest, RetrieveMonthlyPayrollOptionsRequest, RetrieveMonthlyPayrollReply,
    RetrieveMonthlyPayrollRequest,
    RetrieveMonthlyPayrollSummaryReply,
    RetrieveMonthlyPayrollSummaryRequest,
    RetrieveNextServicioListReply,
    RetrieveNextServicioMissionInfoBody,
    RetrieveNextServicioMissionInfoReply,
    RetrieveNextServicioReply, RetrieveNextServicioTableInfoBody,
    RetrieveNextServicioTableInfoReply, RetrievePayrollOptionsReply, RetrievePlanningAreaListReply,
    RetrievePresenceConfigurationReply,
    RetrieveServiceJobListReply,
    RetrieveServiceUserAssignmentBody,
    RetrieveServiceUserAssignmentReply, RetrieveServiceWorkplaceUserAssignmentBody,
    RetrieveServiceWorkplaceUserAssignmentReply, RetrieveUserAreaListReply,
    SearchBusOdcReply,
    SearchWorkers,
    SearchWorkersReply,
    SendQRToMissionUserRequest,
    ServiceCard,
    ServiceCardList, ServiceJob, ServiceListFilters, ServicesBoard,
    ServiceStatus,
    ServiceStatusEnum,
    ServiceUserAssignment,
    ServiceUserAssignmentReply, SynchronizeServiceWorkplaceUserAssignmentRequest, UploadResourcesExcelBody,
    UploadResourcesModelResponse
} from './services.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../../../core/dtype/dtype.service';
import { HttpService } from '../../../core/http/http.service';
import { LocalizePipe } from '../../../core/pipes/localize.pipe';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';
import { CompanyUser } from '../../../core/user/companyUser.model';
import { Utils } from '../../../core/utils/utils';
import { TASK_TYPE_URI } from '../../../core/task-type/task-type.types';
import { sendQRMailByDNIReply } from '../../nextstaff/nextstaff.model';

export interface ServiceListOptions {
    activeOnly?: boolean;
    from?: string;
    to?: string;
    status?: string[];
}

export const SERVICE_URI = 'http://grupoconstant.com/next/NextServicio/0.0.1';
export const SERVICE_DTYPE_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextservicio.NextServicio';
export const INFO_SERVICE_DTYPE_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextservicio.NextServicioMissionInfo';
export const SERVICE_JOB_DTYPE_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextservicio.NextServicioJob';
export const SERVICE_AVAILABILITY_DTYPE_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.availabilitycontrol.AvailabilityControl';
export const SERVICE_PRESENCE_CONFIGURATION_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.feature.payroll.config.NextServicioPresenceConfiguration';
export const SERVICE_PRESENCE_CONCEPT_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.feature.payroll.config.NextServicioPresenceConcepts';
export const ABSENCE_TYPES_MASTER_URI = 'master://OU=01/AbsenceTypes';
export const SERVICE_WORKPLACE_USER_ASSIGNMENT_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.workplaceuserassignment.ServiceWorkplaceUserAssignment';

@Injectable({
    providedIn: 'root'
})
export class ServicesService {
    readonly DTYPE_BRAND_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextmarca.NextMarca';
    readonly DTYPE_CUSTOMER_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextcliente.NextCliente';
    readonly DTYPE_PROJECT_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextproyecto.NextProyecto';
    readonly DTYPE_SERVICE_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextservicio.NextServicio';
    readonly userUri = 'https://xml.cloudcodice.com/feature/company/User/1.1.0';
    readonly backofficeProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileBackoffice';
    readonly viewerProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileViewer';
    readonly busodcUri = 'http://grupoconstant.com/minibus/BusOdc/0.0.1';
    readonly subdivisionUri = 'http://xml.cloudcodice.com/type/location/Subdivision/1.1.0';
    readonly stateData = [
        ServiceStatusEnum.design,
        ServiceStatusEnum.planning,
        ServiceStatusEnum.inProgress,
        ServiceStatusEnum.finished,
        ServiceStatusEnum.abandoned,
        ServiceStatusEnum.historical
    ];

    private service: BehaviorSubject<NextServicio | null> = new BehaviorSubject(null);
    private services: BehaviorSubject<NextServicio[] | null> = new BehaviorSubject(null);
    private board: BehaviorSubject<ServicesBoard | null> = new BehaviorSubject(null);
    private filteredBoard: BehaviorSubject<ServicesBoard | null> = new BehaviorSubject(null);
    private listServiceCard: ServiceCardList[];
    private viewListServiceCard: BehaviorSubject<ServiceCardList[] | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(undefined);
    private planningAreas: BehaviorSubject<PlanningArea[] | null> = new BehaviorSubject(null);
    private drawerStatus: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private serviceListFilters: BehaviorSubject<ServiceListFilters | null> = new BehaviorSubject(null);
    private viewMode: BehaviorSubject<string> = new BehaviorSubject('kanban');

    private readonly tableInfoTypeClass = 'com.cloudcodice.thirdparty.grupoconstant.backoffice.services.service.tableinfo.ServiceTableInfoType';

    constructor(
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private httpService: HttpService,
        private localize: LocalizePipe,
        private translocoService: TranslocoService,
        private router: Router,
        private navigationService: NavigationService
    ) { }

    get service$(): Observable<NextServicio> {
        return this.service.asObservable();
    }

    get services$(): Observable<NextServicio[]> {
        return this.services.asObservable();
    }

    get board$(): Observable<ServicesBoard> {
        return this.board.asObservable();
    }

    get filteredBoard$(): Observable<ServicesBoard> {
        return this.filteredBoard.asObservable();
    }

    get planningAreas$(): Observable<PlanningArea[]> {
        return this.planningAreas.asObservable();
    }

    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    get viewListServiceCard$(): Observable<ServiceCardList[]> {
        return this.viewListServiceCard.asObservable();
    }

    get drawerStatus$(): Observable<boolean> {
        return this.drawerStatus.asObservable();
    }

    get serviceListFilters$(): Observable<ServiceListFilters> {
        return this.serviceListFilters.asObservable();
    }

    get viewMode$(): Observable<string> {
        return this.viewMode.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    setDrawerStatus(status: boolean): void {
        this.drawerStatus.next(status);
    }

    setViewMode(mode: string): void {
        this.viewMode.next(mode);
    }

    setServiceListFilters(filters: ServiceListFilters): void {
        this.serviceListFilters.next(filters);
    }

    applyServiceListFilters(): ServiceCardList[] {
        let filteredServiceCardList;
        if (!this.serviceListFilters.value) {
            return this.listServiceCard;
        }
        if (this.serviceListFilters.value?.status?.length > 0) {
            filteredServiceCardList = this.applyServiceListFilterStatus(this.listServiceCard, this.serviceListFilters.value?.status);
            if (filteredServiceCardList.length === 0) {
                return filteredServiceCardList;
            }
        }
        return filteredServiceCardList ? filteredServiceCardList : this.listServiceCard;
    }

    applyServiceListFilterStatus(list: ServiceCardList[], status: string[]): ServiceCardList[] {
        return list.filter(item => status.includes(item.status));
    }

    applyServiceListFilterDates(list: ServiceCardList[], dateFrom: string, dateTo: string): ServiceCardList[] {
        if (dateFrom === '') {
            return list.filter(item => item.dateFrom <= dateTo || item.dateTo <= dateTo);
        }
        if (dateTo === '') {
            return list.filter(item => item.dateFrom >= dateFrom || item.dateTo >= dateFrom);
        }
        return list.filter(item => !(item.dateFrom < dateFrom && item.dateTo < dateTo) && !(item.dateFrom > dateTo && item.dateTo > dateTo));
    }

    getService(serviceId: string): Observable<NextServicio> {
        const args = { serviceId };

        return this.httpService.post<RetrieveNextServicioReply>(NextMethodsEnum.retrieveNextServicio, args)
            .pipe(catchError((error) => {
                console.error(error);
                let _message = null;
                if (error?.status === 404) {
                    this.navigationService.removeRecentService(serviceId);
                    this.router.navigate(['/404']);
                    _message = this.translocoService.translate('serviceNotFound');
                }
                if (error?.status === 403) {
                    this.navigationService.removeRecentService(serviceId);
                    this.router.navigate(['/403']);
                    _message = this.translocoService.translate('serviceForbidden');
                }
                this.dialogsService.openErrorDialog({
                    message: _message ? _message : undefined,
                });
                return throwError(() => error);
            }),
                map((response) => {
                    const service = response?.nextServicio;

                    if (response?.partaker) {
                        const brandPartakers = response?.partaker[DTYPE_BRAND_URI];
                        service.brand = brandPartakers[service.marca_code] as NextBrand;
                        if (service.brand?.logo?.startsWith('http')) {
                            service.brand.logo = this.httpService.getBaseUrl() + service.brand.logo;
                        }

                        const clientPartakers = response?.partaker[DTYPE_CUSTOMER_URI];
                        service.customer = clientPartakers[`${service.marca_code}+${service.cliente_code}`] as NextCustomer;
                        if (service.customer?.logo?.startsWith('http')) {
                            service.customer.logo = this.httpService.getBaseUrl() + service.customer.logo;
                        }

                        const projectPartakers = response?.partaker[DTYPE_PROJECT_URI];
                        service.project = projectPartakers[service.projectId] as NextProject;
                        if (service.project?.logo?.startsWith('http')) {
                            service.project.logo = this.httpService.getBaseUrl() + service.project.logo;
                        }

                        if (response.nextServicio) {
                            response.nextServicio.mailDebugModeUsers = response.nextServicio.mailDebugModeUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                            response.nextServicio.mailBCCUsers = response.nextServicio.mailBCCUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                        }
                    }

                    this.createProfileFields(service, response.partaker);

                    service.statusLabel = ServiceStatus[service.status].label;

                    return service;
                }),
                tap(service => this.service.next(service))
            );
    }

    getServices(projectId: string, opts: ServiceListOptions = {}): Observable<NextServicio[]> {
        const args = { projectId, ...opts };

        return this.httpService.post<RetrieveNextServicioListReply>(NextMethodsEnum.retrieveNextServicioList, args)
            .pipe(
                map(response => response?.nextServicio ?? []),
                tap(services => this.services.next(services))
            );
    }

    getServicesBoard(projectId: string, opts: ServiceListOptions = {}): Observable<ServicesBoard> {
        const args = { projectId, ...opts };
        return this.httpService.post<RetrieveNextServicioListReply>(NextMethodsEnum.retrieveNextServicioList, args)
            .pipe(
                map((reply) => {
                    let brand: NextBrand;
                    if (reply?.partaker) {
                        const brandPartakers = reply.partaker[DTYPE_BRAND_URI];
                        brand = brandPartakers[Object.keys(brandPartakers)[0]] as NextBrand;
                        if (brand?.logo?.startsWith('http')) {
                            brand.logo = this.httpService.getBaseUrl() + brand.logo;
                        }
                    }

                    let client: NextCustomer;
                    if (reply?.partaker) {
                        const clientPartakers = reply.partaker[DTYPE_CUSTOMER_URI];
                        client = clientPartakers[Object.keys(clientPartakers)[0]] as NextCustomer;
                        if (client?.logo?.startsWith('http')) {
                            client.logo = this.httpService.getBaseUrl() + client.logo;
                        }
                    }

                    let project: NextProject;
                    if (reply?.partaker) {
                        const projectPartakers = reply.partaker[DTYPE_PROJECT_URI];
                        project = projectPartakers[Object.keys(projectPartakers)[0]] as NextProject;
                        if (project?.logo?.startsWith('http')) {
                            project.logo = this.httpService.getBaseUrl() + project.logo;
                        }
                    }

                    const lists: List[] = [];
                    for (const state of this.stateData) {
                        lists.push(new List(ServiceStatus[state].label, state, reply?.nextServicio?.filter(o => o.status === state)));
                    }

                    return {
                        brand,
                        client,
                        project,
                        lists
                    };
                }),
                tap((board) => {
                    this.board.next(board);
                    this.filterServicesBoard();
                    this.listServiceCard = this.updateViewListBoard(board);
                    this.getViewListBoard();
                })
            );
    }

    filterServicesBoard(search?: string) {
        search = search ?? '';
        const board = cloneDeep(this.board.getValue());
        for (const list of board.lists) {
            list.cards = list.cards.filter(c => Utils.removeAccents(c.name).toUpperCase().includes(Utils.removeAccents(search).toUpperCase())
            && c.role);
        }
        this.filteredBoard.next(board);
        this.listServiceCard = this.updateViewListBoard(board);
        this.getViewListBoard();
    }

    getViewListBoard(page: number = 0, size: number = 10, field: string = 'name', dir: SortDirection = 'asc'): Observable<ServiceCardList[]> {

        //Apply filters
        // Clone the list to prevent accidental updates
        let list = cloneDeep(this.sortListView(this.listServiceCard, field, dir));
        list = list.filter(l => l.role);
        const listLength = list.length;

        // Paginate - Start

        // Calculate pagination details
        const begin = page * size;
        const end = Math.min((size * (page + 1)), listLength);
        const lastPage = Math.max(Math.ceil(listLength / size), 1);

        // Prepare the pagination object
        let pagination: Partial<Pagination>;

        // If the requested page number is bigger than
        // the last possible page number, return null for
        // tasks but also send the last possible page so
        // the app can navigate to there
        if (page > lastPage) {
            // tasks = null;
            pagination = {
                lastPage
            };
        } else {
            // Paginate the results by 10
            list = list.slice(begin, end);

            // Prepare the pagination mock-api
            pagination = {
                length: listLength,
                size,
                page,
                lastPage: lastPage,
                startIndex: begin,
                endIndex: end - 1
            };
        }

        this._pagination.next(pagination as Pagination);
        this.viewListServiceCard.next(list);
        return of(list);
    }

    createService(service: NextServicio): Observable<NextServicio> {
        const nextServicio = cloneDeep(service);

        if (Array.isArray(nextServicio.backoffice) && nextServicio.backoffice.length > 0) {
            nextServicio[this.backofficeProfileUri] =
                nextServicio.backoffice.map(d => d.userId);
            delete nextServicio.backoffice;
        }
        if (Array.isArray(nextServicio.viewer) && nextServicio.viewer.length > 0) {
            nextServicio[this.viewerProfileUri] =
                nextServicio.viewer.map(p => p.userId);
            delete nextServicio.viewer;
        }
        if (Array.isArray(nextServicio.mailDebugModeUsers) && nextServicio.mailDebugModeUsers.length > 0) {
            nextServicio.mailDebugModeUsers = nextServicio.mailDebugModeUsers.map(user => typeof user === 'string' ? user : user.userId);
        }
        if (Array.isArray(nextServicio.mailBCCUsers) && nextServicio.mailBCCUsers.length > 0) {
            nextServicio.mailBCCUsers = nextServicio.mailBCCUsers.map(user => typeof user === 'string' ? user : user.userId);
        }
        const args = { nextServicio };

        return this.httpService.post<FlushNextServicioReply>(NextMethodsEnum.flushNextServicio, args)
            .pipe(
                map((response) => {
                    const service = response?.nextServicio;

                    if (response?.partaker) {
                        const brandPartakers = response?.partaker[DTYPE_BRAND_URI];
                        service.brand = brandPartakers[service.marca_code] as NextBrand;
                        if (service.brand?.logo?.startsWith('http')) {
                            service.brand.logo = this.httpService.getBaseUrl() + service.brand.logo;
                        }

                        const clientPartakers = response?.partaker[DTYPE_CUSTOMER_URI];
                        service.customer = clientPartakers[`${service.marca_code}+${service.cliente_code}`] as NextCustomer;
                        if (service.customer?.logo?.startsWith('http')) {
                            service.customer.logo = this.httpService.getBaseUrl() + service.customer.logo;
                        }

                        const projectPartakers = response?.partaker[DTYPE_PROJECT_URI];
                        service.project = projectPartakers[service.projectId] as NextProject;
                        if (service.project?.logo?.startsWith('http')) {
                            service.project.logo = this.httpService.getBaseUrl() + service.project.logo;
                        }

                        if (response.nextServicio) {
                            response.nextServicio.mailDebugModeUsers = response.nextServicio.mailDebugModeUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                            response.nextServicio.mailBCCUsers = response.nextServicio.mailBCCUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                        }
                    }

                    this.createProfileFields(service, response.partaker);

                    service.statusLabel = ServiceStatus[service.status].label;

                    return service;
                }),
                tap(savedService => this.addNewServiceToBoard(savedService))
            );
    }

    updateService(service: NextServicio): Observable<NextServicio> {
        const nextServicio = cloneDeep(service);

        if (Array.isArray(nextServicio.backoffice) && nextServicio.backoffice.length > 0) {
            nextServicio[this.backofficeProfileUri] =
                nextServicio.backoffice.map(d => d.userId);
            delete nextServicio.backoffice;
        }
        if (Array.isArray(nextServicio.viewer) && nextServicio.viewer.length > 0) {
            nextServicio[this.viewerProfileUri] =
                nextServicio.viewer.map(p => p.userId);
            delete nextServicio.viewer;
        }
        if (Array.isArray(nextServicio.mailDebugModeUsers) && nextServicio.mailDebugModeUsers.length > 0) {
            nextServicio.mailDebugModeUsers = nextServicio.mailDebugModeUsers.map(user => typeof user === 'string' ? user : user.userId);
        }
        if (Array.isArray(nextServicio.mailBCCUsers) && nextServicio.mailBCCUsers.length > 0) {
            nextServicio.mailBCCUsers = nextServicio.mailBCCUsers.map(user => typeof user === 'string' ? user : user.userId);
        }
        const args = { nextServicio };

        return this.httpService.post<FlushNextServicioReply>(NextMethodsEnum.flushNextServicio, args)
            .pipe(
                map((response) => {
                    const service = response?.nextServicio;

                    if (response?.partaker) {
                        const brandPartakers = response?.partaker[DTYPE_BRAND_URI];
                        service.brand = brandPartakers[service.marca_code] as NextBrand;
                        if (service.brand?.logo?.startsWith('http')) {
                            service.brand.logo = this.httpService.getBaseUrl() + service.brand.logo;
                        }

                        const clientPartakers = response?.partaker[DTYPE_CUSTOMER_URI];
                        service.customer = clientPartakers[`${service.marca_code}+${service.cliente_code}`] as NextCustomer;
                        if (service.customer?.logo?.startsWith('http')) {
                            service.customer.logo = this.httpService.getBaseUrl() + service.customer.logo;
                        }

                        const projectPartakers = response?.partaker[DTYPE_PROJECT_URI];
                        service.project = projectPartakers[service.projectId] as NextProject;
                        if (service.project?.logo?.startsWith('http')) {
                            service.project.logo = this.httpService.getBaseUrl() + service.project.logo;
                        }

                        if (response.nextServicio) {
                            response.nextServicio.mailDebugModeUsers = response.nextServicio.mailDebugModeUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                            response.nextServicio.mailBCCUsers = response.nextServicio.mailBCCUsers?.map(u => {
                                if (typeof u === 'string') {
                                    const user: CompanyUser = { ...response.partaker[USER_URI][u] };
                                    return user;
                                }
                                return u;
                            }) ?? [];
                        }
                    }

                    this.createProfileFields(service, response.partaker);

                    service.statusLabel = ServiceStatus[service.status].label;

                    return service;
                }),
                tap(savedService => this.service.next(savedService))
            );
    }

    exportService(service: NextServicio): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.exportNextServicio, {serviceId: service.serviceId}, 'blob');
    }

    importService(body: ImportNextServicioBody): Observable<ImportNextServicioResponse> {
        return this.httpService.post<ImportNextServicioResponse>(NextMethodsEnum.importNextServicio, body);
    }

    async deleteService(serviceId: string): Promise<boolean> {
        try {
            const reply = await firstValueFrom(this.httpService.post(NextMethodsEnum.deleteNextServicio, { serviceId: serviceId }));
            if (reply.deletedKey) {
                const board = this.board.getValue();
                let index: number;
                for (const list of board.lists) {
                    index = list.cards.findIndex(s => s.serviceId === reply.deletedKey);
                    if (index > -1) {
                        list.cards.splice(index, 1);
                        break;
                    }
                }
                this.board.next(board);
                const filteredBoard = this.filteredBoard.getValue();
                for (const list of filteredBoard.lists) {
                    index = list.cards.findIndex(s => s.serviceId === reply.deletedKey);
                    if (index > -1) {
                        list.cards.splice(index, 1);
                        break;
                    }
                }
                this.filteredBoard.next(filteredBoard);
                this.listServiceCard = this.updateViewListBoard(filteredBoard);
                this.getViewListBoard();
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo ? this.localize.transform(error.error.echo) : undefined,
            });
            return false;
        }
    }

    changeServiceStatus(args: { serviceId: string; status: ServiceStatusEnum }): Observable<NextServicio> {
        return this.httpService.post<ChangeServiceStatusReply>(NextMethodsEnum.changeServiceStatus, args)
            .pipe(
                map(response => response?.nextServicio),
            );
    }

    flushUser2NextServicio(data: { userDatatypeUri: string; user: UserProfilePayload[] }): Observable<UserProfile> {
        return this.httpService.post<FlushUser2NextServicioReply>(NextMethodsEnum.flushUser2NextServicio, data)
            .pipe(
                map((reply) => {
                    const users = reply?.user2NextServicio?.map((u) => {
                        const user: UserProfile = { ...reply.partaker[this.userUri][u.userId], ...u };
                        if (user.grantor && user.grantor !== 'system') {
                            user.grantorName = reply.partaker[this.userUri][user.grantor].displayName;
                        }
                        if (!user.profileImage?.startsWith('http')) {
                            user.profileImage = this.httpService.getBaseUrl() + user.profileImage;
                        }
                        return user;
                    });
                    return users[0];
                })
            );
    }

    getProfileNameByUri(uri: string): string {
        if (uri.toLowerCase().includes('backoffice')) {
            return 'backoffice';
        } else if (uri.toLowerCase().includes('viewer')) {
            return 'viewer';
        }
    }

    getPlanningAreaList(serviceId: string): Observable<PlanningArea[]> {
        return this.httpService.post<RetrievePlanningAreaListReply>(NextMethodsEnum.retrievePlanningAreaList, { serviceId })
            .pipe(
                map((reply) => {
                    const areas = reply?.planningArea?.map((a) => {
                        const object = reply.partaker?.[reply.uriPlanningArea]?.[a.planningArea];
                        a.name = object?.value;
                        a.uri = reply.uriPlanningArea;
                        return a;
                    }) ?? [];
                    this.planningAreas.next(areas);
                    return areas;
                })
            );
    }

    flushPlanningArea(request: FlushPlanningAreaRequest) {
        return this.httpService.post<FlushPlanningAreaReply>(NextMethodsEnum.flushPlanningArea, request)
            .pipe(
                map((reply) => {
                    if (!reply?.planningArea) {
                        return undefined;
                    }
                    const object = reply.partaker[reply.uriPlanningArea]?.[reply.planningArea.planningArea];
                    reply.planningArea.name = object?.value;
                    return reply.planningArea;
                })
            );
    }

    deletePlanningArea(serviceId: string, planningArea: string): any {
        return this.httpService.post<any>(NextMethodsEnum.deletePlanningArea, { serviceId, planningArea });
    }

    getUserAreaList(service: NextServicio): Observable<NextServicioUserAreaFront[]> {
        return this.httpService.post<RetrieveUserAreaListReply>(NextMethodsEnum.retrieveNextServicioUserAreaList, { serviceId: service.serviceId })
            .pipe(
                map((reply) => {
                    const areas = reply?.userArea?.map((a) => {
                        const odc = reply.partaker[this.busodcUri][a.odc_code];
                        a.odc_name = odc?.displayName;
                        a.planningAreaForDisplay = a.planningArea.map(pa => reply.partaker[service.uriPlanningArea]?.[pa]);
                        return a;
                    }) ?? [];
                    return areas;
                })
            );
    }

    flushUserArea(body: FlushUserAreaBody, uriPlanningArea: string): Observable<NextServicioUserAreaFront[]> {
        return this.httpService.post<RetrieveUserAreaListReply>(NextMethodsEnum.flushNextServicioUserArea, body)
            .pipe(
                map((reply) => {
                    const areas = reply?.userArea?.map((a) => {
                        const odc = reply.partaker[this.busodcUri][a.odc_code];
                        a.odc_name = odc?.displayName;
                        a.planningAreaForDisplay = a.planningArea.map(pa => reply.partaker[uriPlanningArea]?.[pa]);
                        return a;
                    }) ?? [];
                    return areas;
                })
            );
    }

    deleteUserArea(userArea: NextServicioUserAreaFront) {
        return this.httpService.post<any>(NextMethodsEnum.deleteNextServicioUserArea, {
            serviceId: userArea.serviceId,
            odc_code: userArea.odc_code,
            cepoblacion: userArea.cepoblacion,
            planningArea: userArea.planningArea
        });
    }

    searchBusOdc(expr: string, marca_code: string): Observable<BusOdc[]> {
        return this.httpService.post<SearchBusOdcReply>(NextMethodsEnum.searchBusOdc, { expr, marca_code })
            .pipe(map(reply => reply?.busOdc ?? []));
    }

    searchWorkers(body: SearchWorkers) {
        return this.httpService.post<SearchWorkersReply>(NextMethodsEnum.searchWorkers, body)
            .pipe(map(reply => reply?.workers ?? []));
    }

    retrieveServiceUserAssignment(body: RetrieveServiceUserAssignmentBody) {
        return this.httpService.post<RetrieveServiceUserAssignmentReply>(NextMethodsEnum.retrieveServiceUserAssignmentList, body)
            .pipe(
                map((reply) => {
                    const assignments: { [key: string]: ServiceUserAssignment[] } = { };
                    if (reply?.assignment?.assignment) {
                        for (const [area, assignmentList] of Object.entries(reply?.assignment?.assignment)) {
                            assignments[area] = [];
                            for (const assignment of assignmentList) {
                                const newSupervised: string[] = structuredClone(assignment.supervised ?? []);
                                for (const sup of assignment.supervised ?? []) {
                                    const instances = this.supervisedIsInMorePlanningAreas(sup, reply?.assignment?.assignment);
                                    if (instances > 1) {
                                        for (let i = 1; i < instances; i++) {
                                            newSupervised.push(sup);
                                        }
                                    }
                                }
                                const newSupervisor: string[] = structuredClone(assignment.supervisor ?? []);
                                for (const sup of assignment.supervisor ?? []) {
                                    const instances = this.supervisedIsInMorePlanningAreas(sup, reply?.assignment?.assignment);
                                    if (instances > 1) {
                                        for (let i = 1; i < instances; i++) {
                                            newSupervised.push(sup);
                                        }
                                    }
                                }
                                const newAssigment: ServiceUserAssignment = {
                                    ...assignment,
                                    supervised: newSupervised,
                                    supervisedView: newSupervised,
                                    supervisor: newSupervisor,
                                    supervisorView: newSupervisor,
                                    userName: reply.partaker?.[USER_URI]?.[assignment.userId]?.displayName,
                                    hrid: reply.partaker?.[USER_URI]?.[assignment.userId]?.distinguishedName,
                                    telephoneNumber: reply.partaker?.[USER_URI]?.[assignment.userId]?.telephoneNumber,
                                    userMail: reply.partaker?.[USER_URI]?.[assignment.userId]?.userPrincipalName
                                };
                                assignments[area].push(newAssigment);
                            }
                        }
                    }
                    return assignments;
                })
            );
    }

    flushServiceUserAssignmentList(body: FlushServiceUserAssignmentListBody) {
        return this.httpService.post<FlushServiceUserAssignmentListReply>(NextMethodsEnum.flushServiceUserAssignmentList, body)
        .pipe(
            map((reply) => {
                const retrieveReply = reply?.retrieveServiceUserAssignmentListReply;
                const assignments: { [key: string]: ServiceUserAssignment[] } = { };
                if (retrieveReply?.assignment?.assignment) {
                    for (const [area, assignmentList] of Object.entries(retrieveReply?.assignment?.assignment)) {
                        assignments[area] = [];
                        for (const assignment of assignmentList) {
                            const newSupervised = structuredClone(assignment.supervised ?? []);
                            for (const sup of assignment.supervised ?? []) {
                                const instances = this.supervisedIsInMorePlanningAreas(sup, retrieveReply?.assignment?.assignment);
                                if (instances > 1) {
                                    for (let i = 1; i < instances; i++) {
                                        newSupervised.push(sup);
                                    }
                                }
                            }
                            const newSupervisor: string[] = structuredClone(assignment.supervisor ?? []);
                                for (const sup of assignment.supervisor ?? []) {
                                    const instances = this.supervisedIsInMorePlanningAreas(sup, retrieveReply?.assignment?.assignment);
                                    if (instances > 1) {
                                        for (let i = 1; i < instances; i++) {
                                            newSupervised.push(sup);
                                        }
                                    }
                                }
                            const newAssigment: ServiceUserAssignment = {
                                ...assignment,
                                supervised: newSupervised,
                                supervisedView: newSupervised,
                                supervisor: newSupervisor,
                                supervisorView: newSupervisor,
                                userName: retrieveReply.partaker?.[USER_URI]?.[assignment.userId]?.displayName,
                                hrid: retrieveReply.partaker?.[USER_URI]?.[assignment.userId]?.distinguishedName,
                                telephoneNumber: retrieveReply.partaker?.[USER_URI]?.[assignment.userId]?.telephoneNumber,
                                userMail: retrieveReply.partaker?.[USER_URI]?.[assignment.userId]?.userPrincipalName
                            };
                            assignments[area].push(newAssigment);
                        }
                    }
                }
                return assignments;
            })
        );
    }

    dismissServiceUserAssignment(request: DismissServiceUserAssignmentRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.dismissServiceUserAssignment, request);
    }

    retrieveTaskTypeDtypeForm(values: any, locator?: NextLocator, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: TASK_TYPE_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
    }

    retrieveNextServicioMissionInfo(body: RetrieveNextServicioMissionInfoBody): Observable<Partial<MissionInfo>> {
        return this.httpService.post<RetrieveNextServicioMissionInfoReply>(NextMethodsEnum.retrieveNextServicioMissionInfo, body)
            .pipe(map(reply => reply.missionInfo ?? {}));
    }

    flushNextServicioMissionInfo(body: FlushNextServicioMissionInfoBody) {
        return this.httpService.post<FlushNextServicioMissionInfoReply>(NextMethodsEnum.flushNextServicioMissionInfo, body)
            .pipe(map(reply => reply?.missionInfo ?? {}));
    }

    deleteNextServicioMissionInfo(body: RetrieveNextServicioMissionInfoBody): any {
        return this.httpService.post<DeleteNextServicioMissionInfoReply>(NextMethodsEnum.deleteNextServicioMissionInfo, body);
    }

    retrieveServiceJobDtype(locator: NextLocator) {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: SERVICE_JOB_DTYPE_URI, locator, status: DTypeStatus.DESIGN, bindableOptions: true }});
    }

    retrieveServiceJobDtypeFormFromDType(dtype: DType, values: ServiceJob, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormFromDtype(dtype, values, options);
    }

    retrieveServiceJobDtypeFormByUri(values: ServiceJob, locator: NextLocator, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: SERVICE_JOB_DTYPE_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
    }

    retrieveServicioJobList(serviceId: string) {
        return this.httpService.post<RetrieveServiceJobListReply>(NextMethodsEnum.retrieveNextServicioJobList, { serviceId })
            .pipe(map(reply => reply?.job ?? []));
    }

    flushServicioJob(job: ServiceJob) {
        return this.httpService.post<FlushServiceJobReply>(NextMethodsEnum.flushNextServicioJob, { job })
            .pipe(map(reply => reply?.job ?? []));
    }

    deleteServicioJob(jobKeys: string[]) {
        return this.httpService.post<DeleteServiceJobReply>(NextMethodsEnum.deleteNextServicioJob, { jobKeys })
            .pipe(map(reply => reply?.deletedJobKey ?? []));
    }

    getAbsenceForms(locator: NextLocator, filters?: { from: string, to: string, status: AvailabilityStatusEnum[], absenceType: string[], userId: string[], supervisorId: string[] }) {
        const request: RetrieveAbsenceFormsRequest = {
            locator,
            from: filters?.from,
            to: filters?.to,
            status: filters?.status ?? [],
            absenceType: filters?.absenceType ?? [],
            userId: filters?.userId ?? [],
            supervisorId: filters?.supervisorId ?? []
        };
        return this.httpService.post<RetrieveAbsenceFormReply>(NextMethodsEnum.retrieveAbsenceForms, request)
            .pipe(map(reply => {
                const absences = reply.availabilityControl?.map(ac => {
                    ac.services = ac.serviceId?.map(s => reply.partaker[SERVICE_DTYPE_URI][s])?.filter(s => s) ?? [];
                    ac.userName = reply.partaker[USER_URI][ac.userId]?.displayName ?? '';
                    return ac;
                }) ?? [];
                return absences;
            }));
    }

    flushAbsenceReview(request: FlushAbsenceReviewRequest) {
        return this.httpService.post<FlushAbsenceReviewReply>(NextMethodsEnum.flushAbsenceReview, request);
    }

    cancelAbsenceDay(request: CancelAbsenceDayRequest) {
        return this.httpService.post<CancelAbsenceDayReply>(NextMethodsEnum.cancelAbsenceDay, request);
    }

    getPresenceConfiguration(serviceId: string, date?: string) {
        return this.httpService.post<RetrievePresenceConfigurationReply>(NextMethodsEnum.retrieveNextPresenceConfiguration, { serviceId, date })
            .pipe(
                map(reply => {
                    if (reply.configuration) {
                        reply.configuration.validators = reply.configuration.validators?.map(u => {
                            if (typeof u === 'string') {
                                const user: CompanyUser = { ...reply.partaker[USER_URI][u] };
                                return user;
                            }
                            return u;
                        }) ?? [];
                    }
                    reply.concepts = reply.concepts ?? [];
                    // if (reply.concepts.length === 0) {
                    //     this.addPresetConceptsToReply(reply, serviceId);
                    // }
                    return reply;
                })
            );
    }

    flushPresenceConfiguration(request: FlushPresenceConfiguration) {
        return this.httpService.post<RetrievePresenceConfigurationReply>(NextMethodsEnum.flushNextPresenceConfiguration, request)
            .pipe(
                map(reply => {
                    if (reply.configuration) {
                        reply.configuration.validators = reply.configuration.validators?.map(u => {
                            if (typeof u === 'string') {
                                const user: CompanyUser = { ...reply.partaker[USER_URI][u] };
                                return user;
                            }
                            return u;
                        }) ?? [];
                    }
                    reply.concepts = reply.concepts ?? [];
                    return reply;
                })
            );
    }

    retrieveDailyPayroll(request: RetrieveDailyPayrollRequest) {
        return this.httpService.post<RetrieveDailyPayrollReply>(NextMethodsEnum.retrieveDailyPayrolls, request)
            .pipe(
                map(reply => {
                    reply.dailyPayroll = reply.dailyPayroll?.map(p => {
                        p.userName = reply.partaker[USER_URI][p.userId]?.displayName;
                        p.userSAMAccountName = reply.partaker[USER_URI][p.userId]?.sAMAccountName;
                        p.validatorUserName = reply.partaker[USER_URI][p.validatorUserId]?.displayName;
                        p.supervisorName = reply.partaker[USER_URI][p.supervisor]?.displayName;
                        return p;
                    }) ?? [];
                    return reply.dailyPayroll;
                })
            );
    }

    flushDailyPayroll(request: FlushDailyPayrollRequest) {
        return this.httpService.post<RetrieveDailyPayrollReply>(NextMethodsEnum.flushDailyPayroll, request)
            .pipe(
                map(reply => {
                    reply.dailyPayroll = reply.dailyPayroll?.map(p => {
                        p.userName = reply.partaker[USER_URI][p.userId]?.displayName;
                        p.userSAMAccountName = reply.partaker[USER_URI][p.userId]?.sAMAccountName;
                        p.validatorUserName = reply.partaker[USER_URI][p.validatorUserId]?.displayName;
                        p.supervisorName = reply.partaker[USER_URI][p.supervisor]?.displayName;
                        return p;
                    }) ?? [];
                    return reply.dailyPayroll;
                })
            );
    }

    retrieveDailyPayrollSummary(request: RetrieveDailyPayrollSummaryRequest) {
        return this.httpService.post<RetrieveDailyPayrollSummaryReply>(NextMethodsEnum.retrieveDailyPayrollSummary, request)
            .pipe(
                map(reply => {
                    reply.summaryPerPlanningArea = reply.summaryPerPlanningArea ?? {};
                    reply.summaryPerPlanningAreaAndSupervisor = reply.summaryPerPlanningAreaAndSupervisor ?? {};
                    for (const map of Object.values(reply.summaryPerPlanningAreaAndSupervisor)) {
                        for (const supervisor of Object.keys(map)) {
                            map[supervisor].supervisorName = reply.partaker[USER_URI]?.[supervisor]?.displayName ?? '';
                        }
                    }
                    return reply;
                })
            )
    }

    retrieveDailyPayrollOptions(request: RetrieveDailyPayrollOptionsRequest) {
        return this.httpService.post<RetrievePayrollOptionsReply>(NextMethodsEnum.retrieveDailyPayrollOptions, request);
    }

    retrieveMonthlyPayrollOptions(request: RetrieveMonthlyPayrollOptionsRequest) {
        return this.httpService.post<RetrievePayrollOptionsReply>(NextMethodsEnum.retrieveMonthlyPayrollOptions, request);
    }

    retrieveMonthlyPayroll(request: RetrieveMonthlyPayrollRequest) {
        return this.httpService.post<RetrieveMonthlyPayrollReply>(NextMethodsEnum.retrieveMonthlyPayrolls, request)
            .pipe(
                map(reply => {
                    reply.monthlyPayroll = reply.monthlyPayroll?.map(p => {
                        p.userName = reply.partaker[USER_URI][p.userId]?.displayName;
                        p.userSAMAccountName = reply.partaker[USER_URI][p.userId]?.sAMAccountName;
                        p.validatorUserName = reply.partaker[USER_URI][p.validatorUserId]?.displayName;
                        p.supervisorName = reply.partaker[USER_URI][p.supervisor]?.displayName;
                        return p;
                    }) ?? [];
                    return reply.monthlyPayroll;
                })
            );
    }

    flushMonthlyPayroll(request: FlushMonthlyPayrollRequest) {
        return this.httpService.post<RetrieveMonthlyPayrollReply>(NextMethodsEnum.flushMonthlyPayroll, request)
            .pipe(
                map(reply => {
                    reply.monthlyPayroll = reply.monthlyPayroll?.map(p => {
                        p.userName = reply.partaker[USER_URI][p.userId]?.displayName;
                        p.userSAMAccountName = reply.partaker[USER_URI][p.userId]?.sAMAccountName;
                        p.validatorUserName = reply.partaker[USER_URI][p.validatorUserId]?.displayName;
                        p.supervisorName = reply.partaker[USER_URI][p.supervisor]?.displayName;
                        return p;
                    }) ?? [];
                    return reply.monthlyPayroll;
                })
            );
    }

    retrieveMonthlyPayrollSummary(request: RetrieveMonthlyPayrollSummaryRequest) {
        return this.httpService.post<RetrieveMonthlyPayrollSummaryReply>(NextMethodsEnum.retrieveMonthlyPayrollSummary, request)
            .pipe(
                map(reply => {
                    reply.summaryPerPlanningArea = reply.summaryPerPlanningArea ?? {};
                    reply.summaryPerPlanningAreaAndSupervisor = reply.summaryPerPlanningAreaAndSupervisor ?? {};
                    for (const map of Object.values(reply.summaryPerPlanningAreaAndSupervisor)) {
                        for (const supervisor of Object.keys(map)) {
                            map[supervisor].supervisorName = reply.partaker[USER_URI]?.[supervisor]?.displayName ?? '';
                        }
                    }
                    return reply;
                })
            )
    }

    getServiceTableInfo(body: RetrieveNextServicioTableInfoBody) {
        return this.httpService.post<RetrieveNextServicioTableInfoReply>(NextMethodsEnum.retrieveServiceTableInfo, body).pipe(
            map(reply => reply.serviceTableInfo)
        );
    }

    getServiceTableInfoTypeEnum() {
        return this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.tableInfoTypeClass }).pipe(
            map(reply => reply.option)
        );
    }

    cloneService( body: CloneServiceBody) {
        return this.httpService.post<CloneServiceReply>(NextMethodsEnum.cloneNextServicio, body).pipe(
            map(reply => reply)
        );
    }

    downloadResourceModel(body: DownloadResourcesModelBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadServiceResourcesModelXSSF, body, 'blob');
    }

    uploadExcel(body: UploadResourcesExcelBody): Observable<UploadResourcesModelResponse> {
        return this.httpService.post<UploadResourcesModelResponse>(NextMethodsEnum.flushDTypeExcelUpload, body);
    }

    requestServiceSynchronization( body: RequestServiceSynchronizationBody) {
        return this.httpService.post<RequestServiceSynchronizationReply>(NextMethodsEnum.requestServiceSynchronization, body).pipe(
            map(reply => reply)
        );
    }

    retrieveAbsenceFilterOptions(request: { serviceId: string }) {
        return this.httpService.post<RetrieveAvailabilityControlSearchOptionsReply>(NextMethodsEnum.retrieveAvailabilityControlSearchOptions, request);
    }

    /**
     * Returns text, color and icon for the status text in the service card
     *
     * @param service
     */
    getServiceState(service: ServiceCard): {
        text: string;
        color?: 'text-green-600' | 'text-red-600' | 'text-gray-500';
        icon?: string;
    } {
        const today = new Date();
        switch (service.status) {
            case ServiceStatusEnum.design:
            case ServiceStatusEnum.planning:
                const startDate = new Date(service.dateFrom);
                if (startDate > today) {
                    const diffInDays = this.getDateDiffInDays(startDate, today);
                    if (diffInDays > 5) {
                        return { text: this.translocoService.translate('services.board.cards.left', { days: diffInDays }), color: 'text-green-600' };
                    } else {
                        return { text: this.translocoService.translate('services.board.cards.left', { days: diffInDays }), color: 'text-red-600' };
                    }
                } else {
                    const diffInDays = this.getDateDiffInDays(today, startDate) - 1;
                    if (diffInDays === 0) {
                        return { text: this.translocoService.translate('services.board.cards.today'), color: 'text-gray-500' };
                    }
                    return { text: this.translocoService.translate('services.board.cards.late', { days: diffInDays }), color: 'text-red-600' };
                }

            case ServiceStatusEnum.inProgress:
                return { text: this.translocoService.translate('healthy'), color: 'text-green-600', icon: 'iconsmind:fruits' };

            case ServiceStatusEnum.finished:
            case ServiceStatusEnum.historical:
                const endDate = new Date(service.dateTo);
                if ((today > endDate) && (service.dateTo)) {
                    const diffInDays = this.getDateDiffInDays(today, endDate);
                    return { text: this.translocoService.translate('services.board.cards.ago', { days: diffInDays }), color: 'text-gray-500', icon: 'iconsmind:chacked_flag' };
                } else {
                    return { text: '', icon: 'iconsmind:chacked_flag' };
                }

            case ServiceStatusEnum.abandoned:
                return { text: '' };
        }
    }

    addNewServiceToBoard(newService: NextServicio) {
        const board = this.board.getValue();
        for (const list of board.lists) {
            if (list.status === newService.status) {
                list.cards.push(new ServiceCard(newService));
                break;
            }
        }
        this.board.next(board);
        this.filteredBoard.next(board);
        this.listServiceCard = this.updateViewListBoard(board);
        this.getViewListBoard();
    }

    downloadSearchAbsencesXSSF(request: DownloadSearchAbsencesXSSFRequest) {
        return this.httpService.postWithHeaders(NextMethodsEnum.downloadSearchAbsencesXSSF, request, 'blob');
    }

    retrieveServiceWorkplaceUserAssignment(body: RetrieveServiceWorkplaceUserAssignmentBody) {
        return this.httpService.post<RetrieveServiceWorkplaceUserAssignmentReply>(NextMethodsEnum.retrieveServiceWorkplaceUserAssignment, body)
            .pipe(
                map((reply) => {
                    const assignments = reply?.assignments?.filter(assignment => !assignment.deprecated)?.map(assignment => {
                        assignment.userName = reply.partaker?.[USER_URI]?.[assignment.userId]?.displayName;
                        return assignment;
                    }) ?? [];
                    return assignments;
                })
            );
    }

    changeServiceWorkplaceUserAssignmentJob(request: ChangeServiceWorkplaceUserAssignmentJobRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.changeServiceWorkplaceUserAssignmentJob, request);
    }

    retrieveMissionUserQR(request: any) {
        return this.httpService.post<sendQRMailByDNIReply>(NextMethodsEnum.retrieveMissionUserQR, request);
    }

    sendQRToMissionUser(request: SendQRToMissionUserRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.sendQRToMissionUser, request);
    }

    synchronizeServiceWorkplaceUserAssignment(request: SynchronizeServiceWorkplaceUserAssignmentRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.synchronizeServiceWorkplaceUserAssignment, request);
    }

    private createProfileFields(service: NextServicio, partaker: Partaker) {
        for (const key of Object.keys(service).filter(k => k.startsWith('userprofile'))) {
            service[this.getProfileNameByUri(key)] = service[key].map((u) => {
                if (typeof u === 'string') {
                    const user: UserProfile = { ...partaker[this.userUri][u] };
                    user.userId = u;
                    if (user.profileImage && !user.profileImage?.startsWith('http')) {
                        user.profileImage = this.httpService.getBaseUrl() + user.profileImage;
                    }
                    return user;
                } else {
                    const user: UserProfile = { ...partaker[this.userUri][u.userId], ...u };
                    if (user.grantor && user.grantor !== 'system') {
                        user.grantorName = partaker[this.userUri][user.grantor].displayName;
                    }
                    if (user.profileImage && !user.profileImage?.startsWith('http')) {
                        user.profileImage = this.httpService.getBaseUrl() + user.profileImage;
                    }
                    return user;
                }
            });
        }
    }

    private sortListView(list: ServiceCardList[], field: string, dir: SortDirection) {
        return list.sort((a, b) => {
            switch (field) {
                case 'name':
                    return this.compareStrings(a.name ?? '', b.name ?? '', dir);
                case 'dateFrom':
                    return this.compareStrings(a.dateFrom ?? '', b.dateFrom ?? '', dir);
                case 'dateTo':
                    return this.compareStrings(a.dateTo ?? '', b.dateTo ?? '', dir);
                case 'status':
                    return this.compareStrings(a.status ?? '', b.status ?? '', dir);
                case 'margin':
                    return this.compareStrings(a.margin.toString() ?? '', b.margin.toString() ?? '', dir);
            }
            return 0;
        });
    }

    private compareStrings(string1: string, string2: string, dir: SortDirection) {
        if (string1 === '') {
            if (string2 === '') {
                return 0;
            } else {
                return dir === 'asc' ? 0 : -1;
            }
        } else if (string2 === '') {
            if (string1 === '') {
                return 0;
            } else {
                return dir === 'asc' ? -1 : 0;
            }
        } else {
            return dir === 'asc' ? string1.localeCompare(string2) : string2.localeCompare(string1);
        }
    }

    private updateViewListBoard(board: ServicesBoard) {
        const viewListBoard: ServiceCardList[] = [];
        for (const list of board.lists) {
            for (const card of list.cards) {
                viewListBoard.push({
                    serviceId: card.serviceId,
                    name: card.name,
                    status: list.title,
                    dateFrom: card.dateFrom,
                    dateTo: card.dateTo,
                    margin: this.getServiceState(card).text,
                    typology: card.typology,
                    role: card.role
                });
            }
        }
        return viewListBoard;
    }

    private getDateDiffInDays(date1: Date, date2: Date): any {
        const diffTime = Math.abs(date1.getTime() - date2.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    /**
     *
     * @param userId
     * @param assignment
     * @returns number of times the user is in the map
     */
    private supervisedIsInMorePlanningAreas(userId: string, assignment: { [key: string]: ServiceUserAssignmentReply[] }) {
        let instances = 0;
        for (const list of Object.values(assignment)) {
            for (const res of list) {
                if (res.userId === userId) {
                    instances++;
                }
            }
        }
        return instances;
    }
}
